/* eslint-disable jsx-a11y/control-has-associated-label */
import { AppIcon } from '@/components/ui/icons';
import { Button } from '@/features/ui/components/Button';
import { IconButton } from '@/components/ui/IconButton';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import MfaChallengeForm from './MfaChallengeForm';
import BrandLogo from '../../../components/BrandLogo';
import StandaloneSurface from '../../../components/Layout/StandaloneSurface';

interface LoginFormProps {
  onToggleMode: () => void;
  onMfaEnrollRequired?: () => void;
}

const TOKEN_DELIVERY = 'project_manager';
const TOKEN_SUPPLIER = 'vendor';
const TOKEN_PORTAL = 'client';

const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode, onMfaEnrollRequired }) => {
  const {
    login,
    loading,
    isDemoMode,
    mfaStatus,
    verifyMfa,
    cancelMfa,
    pendingMfaEmail
  } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMfaVerifying, setIsMfaVerifying] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await login(formData.email, formData.password);

      if (!result.ok) {
        setError(result.error || 'Login failed');
      } else if (result.mfaEnrollRequired) {
        // User needs to set up 2FA first
        onMfaEnrollRequired?.();
      }
      // If mfaRequired, the mfaStatus will change and we'll show MFA form
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaVerify = async (code: string) => {
    setMfaError('');
    setIsMfaVerifying(true);

    try {
      const result = await verifyMfa(code);
      if (!result.success) {
        setMfaError(result.error || 'Verification failed');
      }
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsMfaVerifying(false);
    }
  };

  const handleMfaCancel = () => {
    setMfaError('');
    cancelMfa();
  };

  // Show MFA challenge form if MFA is required
  if (mfaStatus === 'required' || mfaStatus === 'verifying') {
    return (
      <MfaChallengeForm
        onVerify={handleMfaVerify}
        onCancel={handleMfaCancel}
        error={mfaError}
        isVerifying={isMfaVerifying}
        userEmail={pendingMfaEmail || formData.email}
      />
    );
  }

  const handleDemoLogin = (role: string) => {
    const demoCredentials = {
      platform_owner: { email: 'owner@ax1.capital', password: 'demo123' },
      project_manager: { email: 'sarah.johnson@northstarholdings.eu', password: 'demo123' },
      vendor: { email: 'emily@atlasdelivery.eu', password: 'demo123' },
      client: { email: 'david.wilson@northstarholdings.eu', password: 'demo123' },
      partner: { email: 'lisa.thompson@meridianadvisory.eu', password: 'demo123' },
      investor: { email: 'rachel.nguyen@horizoncapital.eu', password: 'demo123' }
    };

    const credentials = demoCredentials[role as keyof typeof demoCredentials];
    if (credentials) {
      setFormData(credentials);
    }
  };

  return (
    <StandaloneSurface measure="reading">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">

        {/* Logo & Header */}
        <div className="text-center mb-2xl">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mb-lg flex items-center justify-center"
          >
            <BrandLogo variant="auth" />
          </motion.div>
          <p className="text-body mt-2">Projects, Partners & Capital</p>
        </div>

        {/* Demo Mode Notice */}
        {isDemoMode && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-xl card-padding-sm bg-accent-primary/10 border border-accent-primary/30 rounded-lg">
            <div className="flex items-center space-x-sm text-accent-primary">
              <AppIcon name="alertCircle" className="w-4 h-4" />
              <span className="text-sm font-medium">Demo Mode Active</span>
            </div>
            <p className="text-sm text-accent-primary mt-1">
              You're in demo mode. Try different roles or use real authentication.
            </p>
          </motion.div>
        )}

        {/* Login Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card card-padding-lg">
          <form onSubmit={handleSubmit} className="space-xl">
            {/* Email Field */}
            <div>
              <label className="block text-caption mb-sm">Email Address</label>
              <div className="relative">
                <AppIcon name="mail" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted w-5 h-5" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 border border-border-subtle rounded-lg focus-ring transition-all"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-caption mb-sm">Password</label>
              <div className="relative">
                <AppIcon name="lock" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full pl-10 pr-12 py-3 border border-border-subtle rounded-lg focus-ring transition-all"
                  placeholder="Enter your password"
                  required
                />
                <IconButton
                  label={showPassword ? 'Hide password' : 'Show password'}
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  icon={showPassword ? <AppIcon name="eyeOff" className="h-4 w-4" /> : <AppIcon name="eye" className="h-4 w-4" />}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-3 bg-status-danger-soft border border-status-danger/30 rounded-lg">
                <div className="flex items-center space-x-sm text-status-danger-foreground">
                  <AppIcon name="alertCircle" className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={isSubmitting || loading}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Demo Accounts */}
          <div className="mt-8 pt-6 border-t border-border-subtle">
            <p className="text-caption text-center mb-lg">Quick Demo Access</p>
            <div className="grid grid-cols-2 gap-2">
              {([
                { membership: 'platform_owner', label: 'Owner' },
                { membership: TOKEN_DELIVERY, label: 'PM' },
                { membership: TOKEN_SUPPLIER, label: 'Vendor' },
                { membership: TOKEN_PORTAL, label: 'Company' },
              ] as const).map(({ membership, label }) => (
                <button
                  key={membership}
                  onClick={() => handleDemoLogin(membership)}
                  className="px-3 py-2 text-body-sm bg-surface-muted hover:bg-surface-chip rounded-lg transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={onToggleMode}
              className="h-auto px-0"
            >
              Don't have an account? Sign up
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </StandaloneSurface>
  );
};

export default LoginForm;
